import { Flex, Box, Text, Loader } from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { StepCard } from './StepCard';
import { SuitabilitySelectableList } from '../edit/components/SuitabilitySelectableList';
import { UploadPillButton } from '../edit/components/UploadPillButton';
import { StepProps } from '../types';
import { SelectedSwitchRow } from '../edit/components/SelectedSwitchRow';
import { useSelectableUploadItems } from '../hooks/useSelectableUploadItems';
import { useUploadFiles } from '../hooks/useUploadFile';
import { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useAllClientDocuments, type UnifiedDocument } from '../hooks/useAllClientDocuments';
import { getClientId } from '../edit/utils/getClientId';
import { useFilteredItems } from '../hooks/useFilteredItems';
import { useItemSelectionMapping } from '../hooks/useItemSelectionMapping';

export function AdviceStep({ data, setData, filter }: StepProps) {
  const clientId = getClientId(data);
  const { documents = [], isLoading } = useAllClientDocuments(
    data.isFamily && data.family
      ? { type: 'family', familyId: data.family.id }
      : { type: 'client', clientId },
  );

  const files = useMemo<UploadedFile[]>(() => {
    return documents
      .filter(doc => doc.documentType !== 'fact-find' && doc.file)
      .map(mapUnifiedDocumentToUploadedFile);
  }, [documents]);

  const items = useSelectableUploadItems(isLoading ? [] : files);

  const selectedIds = useMemo(() => data.adviceFiles.map(f => f.id), [data.adviceFiles]);

  const selectedCount = selectedIds.length;

  const familyId = data.isFamily ? data.family?.id : undefined;

  const { isUploading, handleFileUpload } = useUploadFiles({
    clientId,
    familyId,
    onUploaded: uploadedFiles => {
      setData(prev => ({
        ...prev,
        adviceFiles: [...prev.adviceFiles, ...uploadedFiles],
      }));
    },
  });

  useEffect(() => {
    if (!items.length) return;

    setData(prev => {
      let changed = false;

      const nextAdviceFiles = prev.adviceFiles.map(f => {
        const item = items.find(i => i.id === f.id);
        const nextName = item?.title ?? 'Untitled file';

        if (f.name !== nextName) {
          changed = true;
          return { ...f, name: nextName };
        }

        return f;
      });

      return changed ? { ...prev, adviceFiles: nextAdviceFiles } : prev;
    });
  }, [items, setData]);

  const filteredItems = useFilteredItems(items, filter);
  const mapIdsToSelectedFiles = useItemSelectionMapping(items);

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard
        title="Select documents that contain your advice / recommendations"
        subTitle="Your advice (taken from meeting notes, recordings, internal notes) will be used to generate the report."
      >
        <Flex align="center" justify="space-between" w="100%" mb="lg" px="xxxs" gap="xxs">
          <SelectedSwitchRow
            selectedCount={selectedCount}
            checked={data.optimiseAdviceReadability}
            onChange={checked =>
              setData(prev => ({
                ...prev,
                optimiseAdviceReadability: checked,
              }))
            }
          />

          <UploadPillButton loading={isUploading} onFilesSelected={handleFileUpload} />
        </Flex>
        <Box flex={1} mih={0}>
          {isLoading ? (
            <Flex justify="center" align="center" h="100%">
              <Loader data-testid="loader" />
            </Flex>
          ) : (
            <SuitabilitySelectableList
              items={filteredItems.map(f => ({
                id: f.id,
                title: f.title,
                description: f.description,
                file: f.file,
              }))}
              selectMultiple
              selectedItemIds={selectedIds}
              setSelectedItemIds={ids =>
                setData(prev => ({
                  ...prev,
                  adviceFiles: mapIdsToSelectedFiles(ids),
                }))
              }
              showReviewed={true}
              highlightedElement={data.highlightedElement}
              emptyView={<Text c="gray.6">No advice found</Text>}
            />
          )}
        </Box>
      </StepCard>
    </Flex>
  );
}

function mapUnifiedDocumentToUploadedFile(doc: UnifiedDocument): UploadedFile {
  if (!doc.file) {
    throw new Error('Document must have a file');
  }

  return {
    id: doc.id,
    clientId: doc.clientId,
    isFolder: false,
    isResolved: doc.backgroundProcess?.status === 'SUCCESS',
    createdAt: doc.createdAt,
    parentId: undefined,
    backgroundProcess: doc.backgroundProcess,
    dataUploadModel: undefined,
    file: doc.file,
  };
}
